import { Button, List, Popconfirm } from 'antd';
import React, { useEffect, useState } from 'react';
import ReminderForm from './ReminderForm';
import {
  formValuesToReminderPayload,
  reminderEditTimeInitialValues,
  type ReminderCreatePayload,
  type ReminderFormValues,
} from './reminderFormUtils';
import type { Reminder } from './types/reminder';
import { MAX_REMINDERS } from './types/reminder';
import type { Student } from './types/student';

type ReminderListProps = {
  reminders: Reminder[];
  students: Student[];
  classId: string;
  teacherId: string;
  onCreate: (payload: ReminderCreatePayload) => Promise<void>;
  onEdit: (
    reminderId: string,
    payload: Partial<Pick<Reminder, 'message' | 'finalReminderTime' | 'softReminderTime' | 'selectedStudents' | 'targetAllStudents'>>
  ) => Promise<void>;
  onDelete: (reminderId: string) => Promise<void>;
  onAddFormOpen?: (open: boolean) => void;
  onEditFormOpen?: (open: boolean) => void;
};

function formatTime(ts: number) {
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export default function ReminderList({
  reminders,
  students,
  classId,
  teacherId,
  onCreate,
  onEdit,
  onDelete,
  onAddFormOpen,
  onEditFormOpen,
}: ReminderListProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const editing = editingId ? reminders.find((r) => r.reminderId === editingId) : null;
  useEffect(() => {
    onAddFormOpen?.(modalOpen);
  }, [modalOpen, onAddFormOpen]);
  useEffect(() => {
    onEditFormOpen?.(!!editingId);
  }, [editingId, onEditFormOpen]);

  const handleCreateSubmit = async (values: ReminderFormValues) => {
    const payload = formValuesToReminderPayload(values, classId, teacherId);
    await onCreate(payload);
    setModalOpen(false);
  };

  const handleEditSubmit = async (values: ReminderFormValues) => {
    if (!editingId || !editing) return;
    const isPastDue = editing.finalReminderTime <= Date.now();
    if (isPastDue) {
      await onEdit(editingId, { message: values.message.trim() });
      setEditingId(null);
      return;
    }
    const payload = formValuesToReminderPayload(
      values,
      editing.classId,
      editing.teacherId
    );
    await onEdit(editingId, {
      message: payload.message,
      finalReminderTime: payload.finalReminderTime,
      softReminderTime: payload.softReminderTime,
      selectedStudents: payload.selectedStudents,
      targetAllStudents: payload.targetAllStudents,
    });
    setEditingId(null);
  };

  const activeReminders = reminders.filter((r) => r.status === 'active');
  const canAdd = activeReminders.length < MAX_REMINDERS;

  const formPanelStyle: React.CSSProperties = {
    marginTop: 8,
    padding: 12,
    borderRadius: 4,
    border: '1px solid #eee',
    background: '#fff',
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        boxSizing: 'border-box',
      }}
    >
      {!modalOpen && !editingId && (
        <div style={{ marginBottom: 8, flexShrink: 0 }}>
          <Button type="primary" onClick={() => setModalOpen(true)} disabled={!canAdd}>
            Add Reminder
          </Button>
          {!canAdd && (
            <span style={{ marginLeft: 8, color: '#999' }}>Only {MAX_REMINDERS} reminder is allowed</span>
          )}
        </div>
      )}

      {modalOpen && (
        <div style={formPanelStyle}>
          <div style={{ marginBottom: 12, fontWeight: 600, fontSize: 14, flexShrink: 0 }}>New Reminder</div>
          <ReminderForm
            students={students}
            existingCount={activeReminders.length}
            onSubmit={handleCreateSubmit}
            onCancel={() => setModalOpen(false)}
          />
        </div>
      )}

      {editingId && editing && (
        <div style={formPanelStyle}>
          <div style={{ marginBottom: 12, fontWeight: 600, fontSize: 14, flexShrink: 0 }}>Edit Reminder</div>
          <ReminderForm
            students={students}
            existingCount={activeReminders.length}
            onSubmit={handleEditSubmit}
            onCancel={() => setEditingId(null)}
            isEditMode={editing.finalReminderTime <= Date.now()}
            initialValues={{
              message: editing.message,
              selectedStudents:
                editing.selectedStudents.length > 0
                  ? editing.selectedStudents
                  : students.map((student) => student.id),
              ...reminderEditTimeInitialValues(editing),
              softReminderMinutes:
                editing.softReminderTime != null
                  ? Math.round((editing.softReminderTime - editing.createdAt) / (60 * 1000))
                  : null,
            }}
          />
        </div>
      )}

      {!modalOpen && !editingId && (
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <List
            dataSource={activeReminders}
            renderItem={(item) => (
              <List.Item
                style={{ zIndex: 999999999 }}
                actions={[
                  <Button type="link" size="small" onClick={() => setEditingId(item.reminderId)}>
                    Edit
                  </Button>,
                  <Popconfirm
                    key="del"
                    title="Delete this reminder?"
                    overlayStyle={{ zIndex: 1000000000 }}
                    onConfirm={() => onDelete(item.reminderId)}
                  >
                    <Button type="link" size="small" danger>
                      Delete
                    </Button>
                  </Popconfirm>,
                ]}
              >
                <List.Item.Meta
                  title={item.message}
                  description={
                    <>
                      Final: {formatTime(item.finalReminderTime)}
                      {item.softReminderTime != null && (
                        <> · Soft: {formatTime(item.softReminderTime)}</>
                      )}
                    </>
                  }
                />
              </List.Item>
            )}
          />
        </div>
      )}
    </div>
  );
}
